import { html, LitElement, type TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import equals from 'ramda/src/equals'
import type { SupportedLocale } from 'zaptic-types'
import sanitizeHtml from 'sanitize-html'
import type { ScheduleFrequency } from '../../../domain/schedule/types.no-deps'
import { omit, validateIso8601String } from '../../../lib/helpers'
import { replaceTokensWithLitHtml } from '../../helpers/lit-template'
import style from '../../style/zaptic-procedure-timeline-event-style'

/**
 * Only certain content types have arguments, shove them in
 * here if you need them to be translated into the output text message
 * the user will see.
 */
const TRANSLATE_ARGUMENT_CONTENT_TYPES = [
    'add-collaborator-to-procedure',
    'remove-collaborator-from-procedure',
    'start-workflow-schedule',
    'edit-workflow-schedule',
    'edit-training-recommendation',
]

export enum Events {
    onClickUser = 'onClickUser',
}

export interface CustomEvents {
    [Events.onClickUser]: CustomEvent<number>
}

export type Content = {
    authorDeleted: boolean
    authorId: number
    authorName: string
    scheduleStartDate?: string
    scheduleDueDate?: string
    scheduleFrequency?: ScheduleFrequency
} & (
    | { type: 'rename-procedure'; oldName: string; newName: string }
    | {
          type: 'add-collaborator-to-procedure' | 'remove-collaborator-from-procedure'
          collaboratorName: string
          collaboratorId: number
          collaboratorDeleted: boolean
      }
    | { type: 'add-team-to-procedure' | 'remove-team-from-procedure'; teamName: string }
    | {
          type:
              | 'create-workflow'
              | 'delete-workflow'
              | 'edit-workflow'
              | 'stop-workflow-schedule'
              | 'edit-workflow-schedule'
          workflowName: string
          workflowType: 'task' | 'training' | 'guide'
      }
    | {
          type: 'start-workflow-schedule'
          workflowName: string
          workflowType: 'task' | 'training' | 'guide'
          scheduleStartDate: string // ISO 8601 string
          scheduleDueDate?: string // ISO 8601 string
          scheduleFrequency?: ScheduleFrequency
      }
    | {
          type: 'edit-training-recommendation'
          taskName: string
          taskProcedureNameIfNotSame?: string
          addedTrainingNames: string[]
          removedTrainingNames: string[]
      }
    | {
          type: 'invalidate-user-training' | 'manually-certify-user-training'
          traineeUserName: string
          trainingName: string
      }
)

function dateTimeHasChanged(newValue: string, oldValue: string) {
    if (!validateIso8601String(newValue)) {
        return false
    }
    return newValue !== oldValue
}

function contenthasChanged(newValue: Content, oldValue: Content) {
    if (newValue.type === 'start-workflow-schedule') {
        if (newValue.scheduleFrequency == null) {
            return validateIso8601String(newValue.scheduleDueDate) && validateIso8601String(newValue.scheduleStartDate)
        } else {
            return validateIso8601String(newValue.scheduleStartDate)
        }
    }
    return !equals(newValue, oldValue)
}

@customElement('procedure-timeline-event')
export class ProcedureTimelineEvent extends LitElement {
    static styles = style

    @property({ hasChanged: dateTimeHasChanged })
    dateTime: string // ISO 8601 string

    @property()
    locale: SupportedLocale

    @property({ hasChanged: contenthasChanged, type: Object })
    content: Content

    @property()
    authorNameType: 'link' | 'plain' = 'plain'

    getIconName() {
        switch (this.content.type) {
            case 'edit-workflow-schedule':
            case 'rename-procedure':
                return 'edit'
            case 'edit-workflow':
            case 'create-workflow':
            case 'delete-workflow':
            case 'start-workflow-schedule':
            case 'stop-workflow-schedule':
                return this.content.workflowType === 'training' ? 'training' : 'task'
            case 'add-collaborator-to-procedure':
            case 'remove-collaborator-from-procedure':
            case 'add-team-to-procedure':
            case 'remove-team-from-procedure':
                return 'team'
            case 'edit-training-recommendation':
            case 'invalidate-user-training':
            case 'manually-certify-user-training':
                return 'training'
        }
    }

    getMessageTranslationSlug(): string {
        switch (this.content.type) {
            case 'rename-procedure':
                return 'shared:procedureTimelineEvent.message.renamedProcedure'
            case 'create-workflow':
                return this.content.workflowType === 'training'
                    ? 'shared:procedureTimelineEvent.message.createdTrainingWorkflow'
                    : 'shared:procedureTimelineEvent.message.createdTaskWorkflow'
            case 'delete-workflow':
                return this.content.workflowType === 'training'
                    ? 'shared:procedureTimelineEvent.message.deletedTrainingWorkflow'
                    : 'shared:procedureTimelineEvent.message.deletedTaskWorkflow'
            case 'edit-workflow':
                return this.content.workflowType === 'training'
                    ? 'shared:procedureTimelineEvent.message.editedTrainingWorkflow'
                    : 'shared:procedureTimelineEvent.message.editedTaskWorkflow'
            case 'start-workflow-schedule':
                return this.content.scheduleFrequency
                    ? 'shared:procedureTimelineEvent.message.startedRecurringSchedule'
                    : 'shared:procedureTimelineEvent.message.startedOneOffSchedule'
            case 'edit-workflow-schedule':
                return this.content.scheduleFrequency
                    ? 'shared:procedureTimelineEvent.message.editedRecurringSchedule'
                    : 'shared:procedureTimelineEvent.message.editedOneOffSchedule'
            case 'stop-workflow-schedule':
                return 'shared:procedureTimelineEvent.message.deletedSchedule'
            case 'add-collaborator-to-procedure':
                return 'shared:procedureTimelineEvent.message.addedCollaboratorToProcedure'
            case 'remove-collaborator-from-procedure':
                return 'shared:procedureTimelineEvent.message.removedCollaboratorFromProcedure'
            case 'add-team-to-procedure':
                return 'shared:procedureTimelineEvent.message.addedTeamToProcedure'
            case 'remove-team-from-procedure':
                return 'shared:procedureTimelineEvent.message.removedTeamFromProcedure'
            case 'edit-training-recommendation': {
                const isDifferentProcedure = !!this.content.taskProcedureNameIfNotSame
                const numAddedTraining = this.content.addedTrainingNames.length
                const numRemovedTraining = this.content.removedTrainingNames.length
                return ProcedureTimelineEvent.getMessageTranslationSlugForEditTrainingRecommendation(
                    isDifferentProcedure,
                    numAddedTraining,
                    numRemovedTraining
                )
            }
            case 'invalidate-user-training':
                return 'shared:procedureTimelineEvent.message.invalidateTraining'
            case 'manually-certify-user-training':
                return 'shared:procedureTimelineEvent.message.manuallyCertifyTraining'
        }
    }

    private static getMessageTranslationSlugForEditTrainingRecommendation(
        isDifferentProcedure: boolean,
        numAddedTraining: number,
        numRemovedTraining: number
    ): string {
        let variation: string
        if (numAddedTraining > 0 && numRemovedTraining > 0) {
            variation = 'AddedAndRemoved'
        } else if (numAddedTraining === 1) {
            variation = 'AddedSingle'
        } else if (numAddedTraining > 1) {
            variation = 'AddedMultiple'
        } else if (numRemovedTraining === 1) {
            variation = 'RemovedSingle'
        } else if (numRemovedTraining > 1) {
            variation = 'RemovedMultiple'
        } else {
            throw new Error(
                `Unsupported: numAddedTraining${numAddedTraining}, numRemovedTraining:${numRemovedTraining}`
            )
        }
        const procedureType = isDifferentProcedure ? 'DifferentProcedure' : ''
        return `shared:procedureTimelineEvent.message.editTrainingRecommendation.${variation}${procedureType}`
    }

    private mapTranslationTokenToElement(token: string): TemplateResult {
        const content = this.content

        switch (token) {
            case '{{authorName}}':
                return html`<zaptic-timeline-user
                    @click=${() => this.onClickUser(content.authorId, content.authorDeleted)}
                    name=${this.content.authorName}
                    type=${this.allowUserToBeALink(content.authorDeleted) ? this.authorNameType : 'plain'}
                ></zaptic-timeline-user>`

            case '{{collaboratorName}}':
                if (
                    content.type === 'add-collaborator-to-procedure' ||
                    content.type === 'remove-collaborator-from-procedure'
                ) {
                    return html`<zaptic-timeline-user
                        @click=${() => this.onClickUser(content.collaboratorId, content.collaboratorDeleted)}
                        name=${content.collaboratorName}
                        type=${this.allowUserToBeALink(content.collaboratorDeleted) ? this.authorNameType : 'plain'}
                    ></zaptic-timeline-user>`
                }

            default:
                throw new Error(`Unsupported token: ${token}`)
        }
    }

    formatDate(date: string) {
        return new Date(Date.parse(date)).toLocaleString(this.locale, {
            month: '2-digit',
            year: '2-digit',
            day: '2-digit',
        })
    }

    getFrequencyTranslationSlug(frequency: ScheduleFrequency) {
        switch (frequency) {
            case 'minutely':
                return 'shared:procedureTimelineEvent.scheduleFrequency.minutely'
            case 'hourly':
                return 'shared:procedureTimelineEvent.scheduleFrequency.hourly'
            case 'daily':
                return 'shared:procedureTimelineEvent.scheduleFrequency.daily'
            case 'weekly':
                return 'shared:procedureTimelineEvent.scheduleFrequency.weekly'
            case 'monthly':
                return 'shared:procedureTimelineEvent.scheduleFrequency.monthly'
            case 'yearly':
                return 'shared:procedureTimelineEvent.scheduleFrequency.yearly'
            case 'onevent':
                return 'shared:procedureTimelineEvent.scheduleFrequency.onEvent'
        }
    }

    getTranslationTokens() {
        const tokens = ['{{authorName}}']

        switch (this.content.type) {
            case 'add-collaborator-to-procedure':
            case 'remove-collaborator-from-procedure':
                tokens.push('{{collaboratorName}}')
        }

        return tokens
    }

    getTranslationArguments() {
        if (!TRANSLATE_ARGUMENT_CONTENT_TYPES.includes(this.content.type)) {
            return this.content
        }

        if (['start-workflow-schedule', 'edit-workflow-schedule'].includes(this.content.type)) {
            return {
                ...this.content,
                scheduleStartDate: this.formatDate(this.content.scheduleStartDate),
                scheduleDueDate: this.content.scheduleDueDate ? this.formatDate(this.content.scheduleDueDate) : null,
                scheduleFrequency: this.content.scheduleFrequency
                    ? window.i18n(this.getFrequencyTranslationSlug(this.content.scheduleFrequency)).toLowerCase()
                    : null,
            }
        }

        if (this.content.type === 'edit-training-recommendation') {
            // N.B. a bit different here in that we include the <b> tags in the translation arguments
            // (rather than using `openingTagForName` etc) because we want to wrap individual list items
            // in tags, rather than the entire formatted list.
            return {
                authorName: `<b>${this.content.authorName}</b>`,
                taskName: `<b>${this.content.taskName}</b>`,
                taskProcedureNameIfNotSame: `<b>${this.content.taskProcedureNameIfNotSame}</b>`,
                addedTrainingNames: this.content.addedTrainingNames.map((name) => `<b>${name}</b>`),
                removedTrainingNames: this.content.removedTrainingNames.map((name) => `<b>${name}</b>`),
            }
        }

        if (
            this.content.type === 'add-collaborator-to-procedure' ||
            this.content.type === 'remove-collaborator-from-procedure'
        ) {
            return omit(this.content, 'collaboratorName')
        }
    }

    allowUserToBeALink(userDeleted: boolean) {
        return !userDeleted
    }

    onClickUser = (userId: number, userDeleted: boolean) => {
        if (!this.allowUserToBeALink(userDeleted)) {
            return
        }
        this.dispatchEvent(new CustomEvent<number>(Events.onClickUser, { detail: userId, composed: true }))
    }

    Message() {
        const message = window
            .i18n(
                sanitizeHtml(
                    // `authorName` is omitted from translation args because it's replaced by an HTML element.
                    window.i18n(this.getMessageTranslationSlug(), omit(this.getTranslationArguments(), 'authorName'))
                ),
                {
                    openingTagForName: '<b>',
                    closingTagForName: '</b>',
                }
            )
            .toString()

        return replaceTokensWithLitHtml(
            message,
            this.getTranslationTokens(),
            this.mapTranslationTokenToElement.bind(this)
        )
    }

    render() {
        return html`
            <zaptic-timeline-event dateTime=${this.dateTime} iconName=${this.getIconName()}>
                <span slot="text">${this.Message()}</span>
            </zaptic-timeline-event>
        `
    }
}
